// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-10-15 17:21:34 +0200 (Wed, 15 Oct 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 157230 $
//  Purpose   : splitter helpers
// =============================================================================

// ---Third-party imports
import React, {Component, Fragment} from 'react'
import {ReflexContainer, ReflexSplitter, ReflexElement} from 'react-reflex'
import clsx from 'clsx'

// ---Framework imports
import {usePersistentShallow, useDebounce, useState, useEffect} from 'uix/hooks'
import log from 'uix/logging'
import {range, sum, isNil, isString} from 'ldash'

// ---Local imports
import S from './splitpane.scss'

// ---Exports

export const SplitPane = ({
  name = 'SplitPane', // used as persistence storeKey (you can also use persist="XXX" directly)
  style,
  children,
  flexInner,
  flex,
  vertical = false, // default is horizontal
  windowResizeAware = false, // setting this to true kills preModified dance
  persist, // store current flex sizes to local store
  sizes, // initial sizes use [0.6, 0.4] for two children with 60% and 40%
  className, // Splitpane className
  classNames, // custom list of classNames for each ReflexElement
  styles, // custom list of styles for each ReflexElement
  overflow, // set this overflow value to outer and inner elements
  borderless,
}) => {
  /* simplified ReflexContainer
  Usage:
    <SplitPane>
      <div>pane1</div>
      <div>pane2</div>
    </SplitPane>
  Usage with Fragments (fragment elements change flexDirection):
    <SplitPane vertical>
      <>
        <Header>Left</Header>
        <div>pane1</div>
      </>
      <>
        <Header>Right</Header>
        <div>pane2</div>
      </>
    </SplitPane>
  */
  if (isString(persist)) {
    name = persist
    persist = true
  }

  let [opts, setOpts] = usePersistentShallow(name, {}, 13)

  let org_num_children = React.Children.count(children)
  let nonEmpty = []
  React.Children.forEach(children, (e, i) => {
    if (!isNil(e) && e !== '') nonEmpty.push(e)
  })
  let num_children = nonEmpty.length

  let stored_flexes = opts[num_children]

  // only persist debounced values
  let [storable, setStorable] = useState(null)
  let [bounced_storable] = useDebounce(storable, 300)
  useEffect(() => {
    if (bounced_storable) {
      let {num_children, values} = bounced_storable
      setOpts({[num_children]: values})
    }
  }, [bounced_storable])

  let flexes = sizes ? sizes : [] // undefined if no persistence
  if (persist && stored_flexes) {
    flexes = stored_flexes
  }
  // keep React-Reflex in control after modifications
  let [preModified, setPreModified] = useState(null)

  // reset preModified if num_children change
  useEffect(() => {
    setPreModified(null)
  }, [num_children])

  if (preModified) {
    flexes = preModified
  }

  let orientation = vertical ? 'vertical' : 'horizontal'
  let innerStyle = flexInner ? {display: 'flex', overflow: 'hidden'} : {}
  let outerStyle = flex ? {...style, flex: '1'} : style
  if (overflow) {
    innerStyle = {...innerStyle, overflow}
    outerStyle = {...outerStyle, overflow}
  }

  const outer = [...flexes] // this looks ugly
  let persistFlex = (index, value) => {
    if (persist && num_children > 1) {
      outer[index] = value
      // maybe this is all nonsense
      let delta = (1 - sum(outer)) / (num_children - 1)
      let values = outer.map((e, i) => (i != index ? e + delta : e))
      setStorable({num_children, values})
      if (!preModified) {
        // stop controlling ReflexElement flex values
        setPreModified(flexes)
      }
    }
  }

  let els = []
  nonEmpty.forEach((e, i) => {
    //~ propagateDimensions
    let istyle = {
      flexDirection: 'column', // always? vertical ? 'column' : 'column',
      ...innerStyle,
      ...(styles?.[i] || {}),
    }
    els.push(
      <ReflexElement
        key={`re_${i}`}
        flex={num_children > 1 ? flexes[i] : 1}
        className={classNames?.[i]}
        style={istyle}
        onResize={event => persistFlex(i, event.component.props.flex)}
      >
        {e}
      </ReflexElement>
    )
    if (i < nonEmpty.length - 1) {
      els.push(<ReflexSplitter key={`rs_${i}`} className={vertical ? S.splitterV : S.splitterH} />)
    }
  })

  if (process.env.UIX_ENV === 'express') {
    // ReflexContainer seems not to work - so fake one
    return (
      <div
        style={outerStyle}
        className={clsx(
          !borderless && S.bordered,
          S.reflexContainer,
          S.ssrReflexContainer,
          vertical ? S.ssrVertical : S.ssrHorizontal
        )}
      >
        {els}
      </div>
    )
  }

  return (
    <ReflexContainer
      windowResizeAware={windowResizeAware}
      style={outerStyle}
      orientation={orientation}
      className={clsx(!borderless && S.bordered, S.reflexContainer, className)}
    >
      {els}
    </ReflexContainer>
  )
}
